// Rechnungsliste im Widget-Stil (design_handoff_rechnungen §4): Kopf, Richtung, Kennzahlen, Filterzeile mit
// Objekt-/Haus-Auswahl und Filter-Fenster, aktive Filter, Karte und Zustände. Nur Darstellung — Zustand, Laden
// und alle Aktionen bleiben in components/InvoicesView.tsx.
import type { ReactNode, Ref } from 'react';
import {
  ArrowClockwise, ArrowCounterClockwise, ArrowLineDown, ArrowLineUp, Bank, Buildings, CalendarBlank, CaretDown, CaretLeft, CaretRight, CircleHalf, CircleNotch,
  Clock, FileText, Funnel, HandCoins, HouseLine, MagnifyingGlass, Sparkle, Stack, User, UsersThree, Warning, WarningCircle, X, type Icon,
} from '@phosphor-icons/react';
import { KennzahlWidget } from '../../../../components/ui/KennzahlWidget';
import { Segment } from '../../../../components/ui/Segment';
import { PillenKnopf } from '../../../../components/ui/PillenKnopf';
import { DropdownPanel, DropdownZeile } from '../../../../components/ui/DropdownPanel';
import { AuswahlKaestchen } from '../../../../components/ui/AuswahlKaestchen';
import { statusLabelDe, statusText, statusTon, type FilterChip, type FilterChipKey, type RechnungsKennzahlen } from '../../../../lib/rechnungenAnsicht/darstellung';

const zahl = (v: number) => `${v.toLocaleString('de-DE')} €`;

// ─── Kopf ──────────────────────────────────────────────────────────────────────

export function RechnungenKopf({ isAdmin, aktualisiert, gesperrt, onAktualisieren, mobil }: {
  isAdmin: boolean; aktualisiert: boolean; gesperrt: boolean; onAktualisieren: () => void; mobil: boolean;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Finanzen</span>
        <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02 }}>Rechnungen</h1>
        <span style={{ fontSize: 14, color: 'var(--ink2)' }}>Belegfluss &amp; Zuständigkeiten</span>
      </div>
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        {isAdmin && (
          <span data-tip="Admin · Alle Belege – Als Admin siehst du alle Belege, unabhängig von Zuständigkeit und Bereich"
            className="inline-flex items-center" style={{ height: 36, padding: '0 14px', gap: 6, borderRadius: 18, fontSize: 12, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
            <Sparkle size={14} weight="fill" /> Admin · Alle Belege
          </span>
        )}
        <button type="button" onClick={onAktualisieren} disabled={gesperrt} data-tip="Aktualisieren – Belege neu laden, ohne die Liste auszublenden"
          className="immo-glas inline-flex items-center"
          style={{ height: mobil ? 44 : 40, padding: '0 16px', gap: 7, borderRadius: 22, border: 'none', cursor: gesperrt ? 'default' : 'pointer', fontFamily: 'inherit',
            fontSize: 13, fontWeight: 700, color: 'var(--ink)', opacity: gesperrt && !aktualisiert ? 0.6 : 1 }}>
          <ArrowClockwise size={14} weight="bold" className={gesperrt ? 'animate-spin' : ''} />
          {aktualisiert ? 'Aktualisiere…' : 'Aktualisieren'}
        </button>
      </div>
    </header>
  );
}

// ─── Richtung ──────────────────────────────────────────────────────────────────

export type RichtungsFilter = 'alle' | 'eingang' | 'ausgang';

export function RichtungWahl({ wert, onWahl, mobil }: { wert: RichtungsFilter; onWahl: (r: RichtungsFilter) => void; mobil: boolean }) {
  return (
    <Segment<RichtungsFilter> ariaLabel="Richtung" art="akzent" hoehe={mobil ? 44 : 34} wert={wert} onChange={onWahl}
      style={{ alignSelf: 'flex-start', background: 'var(--glass)', boxShadow: 'var(--glass-sh)', padding: 4 }}
      eintraege={[
        { key: 'alle', label: 'Alle', icon: Stack, tip: 'Alle Belege' },
        { key: 'eingang', label: 'Eingehend', icon: ArrowLineDown, tip: 'Eingehend – Eingangsrechnungen (Verbindlichkeiten)' },
        { key: 'ausgang', label: 'Ausgehend', icon: ArrowLineUp, tip: 'Ausgehend – Eigene Ausgangsrechnungen (Forderungen)' },
      ]} />
  );
}

// ─── Kennzahlen ────────────────────────────────────────────────────────────────

function SummenZeile({ icon: I, label, betrag, tip }: { icon: Icon; label: string; betrag: number; tip: string }) {
  return (
    <span data-tip={tip} className="flex justify-between" style={{ gap: 8 }}>
      <span className="flex min-w-0 items-center" style={{ gap: 5, color: 'var(--ink2)' }}>
        <I size={12} weight="bold" color="var(--acc)" className="shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="shrink-0 whitespace-nowrap tabular-nums">{zahl(betrag)}</span>
    </span>
  );
}

export function KennzahlenWidgets({ k, status, onStatus }: { k: RechnungsKennzahlen; status: string; onStatus: (s: string) => void }) {
  return (
    <div className="grid" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))' }}>
      <KennzahlWidget label="Offen" wert={zahl(k.offeneSum)} unter={`${k.offeneCount} Belege`} icon={FileText} farbe="var(--warn)"
        aktiv={status === 'offen'} onClick={() => onStatus('offen')} tip="Offen – Belege mit Status offen oder unklar. Klick filtert die Liste">
        <SummenZeile icon={ArrowLineUp} label="Forderungen" betrag={k.offeneSumAusgang} tip="Forderungen – offene eigene Ausgangsrechnungen" />
        <SummenZeile icon={ArrowLineDown} label="Verbindlichkeiten" betrag={k.offeneSumEingang} tip="Verbindlichkeiten – offene Eingangsrechnungen" />
      </KennzahlWidget>
      <KennzahlWidget label="Überfällig" wert={k.overdueCount} unter="Fristen abgelaufen" icon={Clock} wasserzeichen
        farbe={k.overdueCount > 0 ? 'var(--bad)' : 'var(--ink3)'} wertFarbe={k.overdueCount > 0 ? 'var(--bad)' : undefined}
        aktiv={status === 'overdue'} onClick={() => onStatus('overdue')} tip="Überfällig – Fälligkeit abgelaufen, weder bezahlt noch in Klärung. Klick filtert" />
      <KennzahlWidget label="KI-Pool" wert={k.unklarCount} unter="Prüfung nötig" icon={Sparkle} farbe="var(--pur)" wasserzeichen
        aktiv={status === 'unklar'} onClick={() => onStatus('unklar')} tip="KI-Pool – Belege, die nicht automatisch gebucht wurden. Klick filtert" />
    </div>
  );
}

// ─── Filterzeile ───────────────────────────────────────────────────────────────

export function SuchFeld({ wert, onWert }: { wert: string; onWert: (s: string) => void }) {
  return (
    <div className="immo-glas flex items-center" style={{ flex: '1 1 260px', minWidth: 0, height: 44, borderRadius: 22, gap: 8, padding: '0 14px' }}>
      <MagnifyingGlass size={16} color={wert ? 'var(--acc)' : 'var(--ink3)'} className="shrink-0" />
      <input value={wert} onChange={(e) => onWert(e.target.value)} placeholder="Suchen nach Belegnummer, Firma, Objekt oder Betrag..." aria-label="Suchen"
        className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
      {wert && (
        <button type="button" onClick={() => onWert('')} aria-label="Suche leeren" data-tip="Suche leeren" className="inline-flex shrink-0 items-center justify-center"
          style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill3)', color: 'var(--ink)' }}>
          <X size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}

export interface ObjektEintrag { id: string; titel: string; unter: string }

export function ObjektAuswahl({ containerRef, offen, onUmschalten, label, aktiv, suche, onSuche, objekte, gewaehltId, ohneId, ohneAnzahl, onWahl }: {
  containerRef: Ref<HTMLDivElement>; offen: boolean; onUmschalten: () => void; label: string; aktiv: boolean;
  suche: string; onSuche: (s: string) => void; objekte: readonly ObjektEintrag[]; gewaehltId: string | null; ohneId: string; ohneAnzahl: number;
  onWahl: (id: string | null) => void;
}) {
  return (
    <div className="relative" ref={containerRef}>
      <PillenKnopf icon={Buildings} aufklapp offen={offen} aktiv={aktiv} onClick={onUmschalten} maxBreite={240} tip="Objekt – Belege auf ein Objekt einschränken">{label}</PillenKnopf>
      {offen && (
        <DropdownPanel label="Objekt wählen" breite={320}>
          <div className="flex items-center" style={{ height: 40, borderRadius: 13, gap: 8, padding: '0 12px', background: 'var(--fill2)' }}>
            <MagnifyingGlass size={15} color="var(--ink3)" />
            <input autoFocus value={suche} onChange={(e) => onSuche(e.target.value)} placeholder="Objekt suchen…" aria-label="Objekt suchen"
              className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }} />
          </div>
          <div className="immo-scroll flex flex-col" style={{ maxHeight: 320, overflowY: 'auto', gap: 2 }}>
            <DropdownZeile titel="Alle Objekte" fett gewaehlt={gewaehltId === null} onClick={() => onWahl(null)} />
            <DropdownZeile titel={`Ohne Objekt zugeordnet (${ohneAnzahl})`} gewaehlt={gewaehltId === ohneId} onClick={() => onWahl(ohneId)} />
            <div aria-hidden style={{ height: 1, margin: '4px 0', background: 'var(--line)' }} />
            {objekte.map((o) => <DropdownZeile key={o.id} titel={o.titel} unter={o.unter} gewaehlt={gewaehltId === o.id} onClick={() => onWahl(o.id)} />)}
            {objekte.length === 0 && <span style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--ink3)' }}>Kein Objekt gefunden</span>}
          </div>
        </DropdownPanel>
      )}
    </div>
  );
}

export function HausAuswahl({ containerRef, offen, onUmschalten, label, aktiv, haeuser, gewaehltId, ohneId, onWahl }: {
  containerRef: Ref<HTMLDivElement>; offen: boolean; onUmschalten: () => void; label: string; aktiv: boolean;
  haeuser: readonly ObjektEintrag[]; gewaehltId: string | null; ohneId: string; onWahl: (id: string | null) => void;
}) {
  return (
    <div className="relative" ref={containerRef}>
      <PillenKnopf icon={HouseLine} aufklapp offen={offen} aktiv={aktiv} onClick={onUmschalten} maxBreite={220} tip="Haus – Belege auf ein Haus der Liegenschaft einschränken">{label}</PillenKnopf>
      {offen && (
        <DropdownPanel label="Haus wählen" breite={280} style={{ padding: 8, gap: 2 }}>
          <DropdownZeile titel="Alle Häuser" fett gewaehlt={gewaehltId === null} onClick={() => onWahl(null)} />
          {haeuser.map((h) => <DropdownZeile key={h.id} titel={h.titel} unter={h.unter || undefined} gewaehlt={gewaehltId === h.id} onClick={() => onWahl(h.id)} />)}
          <div aria-hidden style={{ height: 1, margin: '4px 0', background: 'var(--line)' }} />
          <DropdownZeile titel="Ohne Haus" fett gewaehlt={gewaehltId === ohneId} onClick={() => onWahl(ohneId)} />
        </DropdownPanel>
      )}
    </div>
  );
}

// ─── Filter-Fenster ────────────────────────────────────────────────────────────

function FilterWahl({ an, onClick, children, warn = false }: { an: boolean; onClick: () => void; children: ReactNode; warn?: boolean }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={an} className="inline-flex items-center"
      style={{ height: 32, padding: '0 12px', gap: 5, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
        background: an ? (warn ? 'var(--bad)' : 'var(--acc-fill)') : (warn ? 'var(--bad-soft)' : 'var(--fill2)'),
        color: an ? (warn ? '#fff' : 'var(--acc-ink)') : (warn ? 'var(--bad)' : 'var(--ink2)'),
        boxShadow: an && !warn ? 'var(--acc-sh)' : 'none' }}>
      {children}
    </button>
  );
}

function FilterGruppe({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
      <div className="flex flex-wrap" style={{ gap: 6 }}>{children}</div>
    </div>
  );
}

const STATUS_WERTE = ['alle', 'bezahlt', 'offen', 'unklar', 'klaerung', 'overdue', 'archiviert', 'zur_pruefung', 'zur_freigabe'] as const;

export interface FilterFensterProps {
  containerRef: Ref<HTMLDivElement>;
  offen: boolean; onUmschalten: () => void; onSchliessen: () => void; aktiv: boolean;
  status: string; onStatus: (s: string) => void;
  zustaendig: string; onZustaendig: (z: string) => void;
  isAdmin: boolean; eigeneId: string;
  mitglieder: readonly { id: string; full_name: string }[]; teams: readonly { id: string; name: string }[];
  nurMahnungen: boolean; onNurMahnungen: () => void;
  nurKorrektur: boolean; onNurKorrektur: () => void;
  nurGuthaben: boolean; onNurGuthaben: () => void;
  nurSepaSkonto: boolean; onNurSepaSkonto: () => void;
  von: string; onVon: (v: string) => void; bis: string; onBis: (v: string) => void;
  onReset: () => void;
}

export function FilterFenster(p: FilterFensterProps) {
  const datum = { height: 40, padding: '0 10px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--fill2)', fontFamily: 'inherit',
    fontSize: 13, fontWeight: 700, color: 'var(--ink)', minWidth: 0, width: '100%' } as const;
  return (
    <div className="relative" ref={p.containerRef}>
      <PillenKnopf icon={Funnel} aufklapp offen={p.offen} aktiv={p.aktiv} onClick={p.onUmschalten} tip="Filter – Status, Zuständigkeit, Typ und Zeitraum">Filter</PillenKnopf>
      {p.offen && (
        <DropdownPanel label="Filter Optionen" ausrichtung="rechts" breite={380} style={{ borderRadius: 24, padding: 16, gap: 16 }}>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 16, fontWeight: 800 }}>Filter Optionen</span>
            <button type="button" onClick={p.onReset} data-tip="Reset – Status, Zuständigkeit, Suche, Objekt, Typ und Zeitraum zurücksetzen"
              style={{ height: 30, padding: '0 12px', borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, background: 'var(--fill2)', color: 'var(--acc)' }}>
              Reset
            </button>
          </div>
          <FilterGruppe label="Status">
            {STATUS_WERTE.map((s) => <FilterWahl key={s} an={p.status === s} onClick={() => p.onStatus(s)}>{statusLabelDe(s)}</FilterWahl>)}
          </FilterGruppe>
          <FilterGruppe label="Zuständigkeit">
            <FilterWahl an={p.zustaendig === 'alle'} onClick={() => p.onZustaendig('alle')}>Alle</FilterWahl>
            <FilterWahl an={p.zustaendig === 'global'} onClick={() => p.onZustaendig('global')}>Global</FilterWahl>
            {p.isAdmin
              ? p.mitglieder.map((m) => <FilterWahl key={m.id} an={p.zustaendig === m.id} onClick={() => p.onZustaendig(m.id)}>{m.full_name.split(' ')[0]}</FilterWahl>)
              : <FilterWahl an={p.zustaendig === p.eigeneId} onClick={() => p.onZustaendig(p.eigeneId)}>Meine</FilterWahl>}
            {p.isAdmin && p.teams.map((t) => (
              <FilterWahl key={t.id} an={p.zustaendig === 't:' + t.id} onClick={() => p.onZustaendig('t:' + t.id)}><UsersThree size={12} weight="bold" />{t.name}</FilterWahl>
            ))}
          </FilterGruppe>
          <FilterGruppe label="Typ">
            <FilterWahl an={p.nurMahnungen} onClick={p.onNurMahnungen}>Nur Mahnungen</FilterWahl>
            <FilterWahl an={p.nurKorrektur} onClick={p.onNurKorrektur}>Korrektur angefordert</FilterWahl>
            <FilterWahl an={p.nurGuthaben} onClick={p.onNurGuthaben}>Nur Guthaben</FilterWahl>
            <FilterWahl an={p.nurSepaSkonto} onClick={p.onNurSepaSkonto} warn>Nur SEPA / Skonto</FilterWahl>
          </FilterGruppe>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Zeitraum (Datum)</span>
            <div className="grid" style={{ gap: 8, gridTemplateColumns: '1fr 1fr' }}>
              <input type="date" value={p.von} onChange={(e) => p.onVon(e.target.value)} aria-label="Von" style={datum} />
              <input type="date" value={p.bis} onChange={(e) => p.onBis(e.target.value)} aria-label="Bis" style={datum} />
            </div>
          </div>
          <button type="button" onClick={p.onSchliessen}
            style={{ height: 42, borderRadius: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
              background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
            Filter schließen
          </button>
        </DropdownPanel>
      )}
    </div>
  );
}

export function GruppierenKnopf({ an, onUmschalten, nachHaus }: { an: boolean; onUmschalten: () => void; nachHaus: boolean }) {
  return (
    <PillenKnopf icon={Stack} aktiv={an} onClick={onUmschalten} tip={nachHaus ? 'Belege nach Haus gruppieren' : 'Belege nach Objekt gruppieren'}>Gruppieren</PillenKnopf>
  );
}

// ─── Aktive Filter ─────────────────────────────────────────────────────────────

const CHIP_ICON: Record<FilterChipKey, Icon> = {
  objekt: Buildings, status: CircleHalf, zustaendig: User, suche: MagnifyingGlass, mahnung: Warning,
  korrektur: ArrowCounterClockwise, guthaben: HandCoins, sepa: Bank, zeitraum: CalendarBlank,
};

export function AktiveFilterLeiste({ chips, onEntfernen, onAlleLoeschen }: { chips: readonly FilterChip[]; onEntfernen: (k: FilterChipKey) => void; onAlleLoeschen: () => void }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)', marginRight: 2 }}>Aktive Filter:</span>
      {chips.map((c) => {
        const I = CHIP_ICON[c.key];
        return (
          <span key={c.key} className="immo-glas inline-flex min-w-0 max-w-full items-center" style={{ height: 30, padding: '0 6px 0 11px', gap: 6, borderRadius: 15, fontSize: 12, fontWeight: 700 }}>
            <I size={12} weight="bold" color="var(--acc)" className="shrink-0" />
            <span className="truncate">{c.label}</span>
            <button type="button" onClick={() => onEntfernen(c.key)} aria-label={`${c.label} entfernen`} data-tip="Filter entfernen"
              className="inline-flex shrink-0 items-center justify-center" style={{ width: 20, height: 20, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
              <X size={9} weight="bold" />
            </button>
          </span>
        );
      })}
      <button type="button" onClick={onAlleLoeschen}
        style={{ height: 30, padding: '0 12px', borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, background: 'transparent', color: 'var(--bad)' }}>
        Alle löschen
      </button>
    </div>
  );
}

// ─── Status-Chip, Karte ────────────────────────────────────────────────────────

export function StatusChip({ status, wiedervorlage, groesse = 24 }: { status: string | null | undefined; wiedervorlage?: string | null; groesse?: number }) {
  const t = statusTon(status);
  return (
    <span className="inline-flex shrink-0 items-center whitespace-nowrap"
      style={{ height: groesse, padding: '0 9px', borderRadius: groesse / 2, fontSize: 11, fontWeight: 800, background: t.flaeche, color: t.farbe }}>
      {statusText({ status, klaerung_wiedervorlage_am: wiedervorlage })}
    </span>
  );
}

export function RechnungsKarte({ kopf, status, wiedervorlage, firma, bezeichnung, meta, gewaehlt, onAuswahl, onOeffnen, touch }: {
  kopf: string; status: string | null | undefined; wiedervorlage?: string | null; firma: string; bezeichnung: string; meta: string;
  gewaehlt: boolean; onAuswahl: () => void; onOeffnen: () => void; touch: boolean;
}) {
  return (
    <div role="button" tabIndex={0} onClick={onOeffnen} data-rechnung-row
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOeffnen(); } }}
      className="immo-glas immo-hover-kachel flex cursor-pointer" style={{ gap: 12, padding: 14, borderRadius: 22, background: gewaehlt ? 'color-mix(in srgb, var(--acc) 10%, var(--glass))' : undefined }}>
      <span style={{ marginTop: 2 }}>
        <AuswahlKaestchen an={gewaehlt} onUmschalten={onAuswahl} label={`${kopf || firma} auswählen`} groesse={24} touch={touch} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 5 }}>
        <div className="flex items-center justify-between" style={{ gap: 8 }}>
          <span className="truncate" style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink3)' }}>{kopf}</span>
          <StatusChip status={status} wiedervorlage={wiedervorlage} />
        </div>
        <span className="truncate" style={{ fontSize: 15.5, fontWeight: 800 }}>{firma}</span>
        {bezeichnung && <span className="truncate" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{bezeichnung}</span>}
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>{meta}</span>
      </div>
    </div>
  );
}

// ─── Zustände ──────────────────────────────────────────────────────────────────

export function ListenLaden() {
  return (
    <div className="flex flex-col items-center" style={{ gap: 12, padding: '90px 0' }}>
      <CircleNotch size={34} weight="bold" color="var(--acc)" className="animate-spin" />
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>Daten werden geladen...</span>
    </div>
  );
}

export function ListenFehler({ text, onErneut, laeuft }: { text: string; onErneut: () => void; laeuft: boolean }) {
  return (
    <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ gap: 10, padding: '40px 24px', borderRadius: 24 }}>
      <span className="inline-flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--bad-soft)' }}>
        <WarningCircle size={26} weight="fill" color="var(--bad)" />
      </span>
      <span style={{ fontSize: 14, fontWeight: 700 }}>{text}</span>
      <button type="button" onClick={onErneut} disabled={laeuft}
        style={{ height: 44, padding: '0 16px', borderRadius: 22, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
        {laeuft ? 'Aktualisiere…' : 'Aktualisieren'}
      </button>
    </div>
  );
}

export function ListenLeer({ onReset }: { onReset: () => void }) {
  return (
    <div className="immo-glas flex flex-col items-center text-center" style={{ gap: 12, padding: '50px 24px', borderRadius: 24 }}>
      <span className="inline-flex items-center justify-center" style={{ width: 58, height: 58, borderRadius: 18, background: 'var(--fill2)' }}>
        <FileText size={28} weight="fill" color="var(--ink3)" />
      </span>
      <span style={{ fontSize: 15, fontWeight: 800 }}>Keine Belege unter diesen Kriterien gefunden</span>
      <button type="button" onClick={onReset}
        style={{ height: 44, padding: '0 16px', borderRadius: 22, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, cursor: 'pointer',
          background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
        Filter zurücksetzen
      </button>
    </div>
  );
}

// ─── Gruppen, Seiten ───────────────────────────────────────────────────────────

export function GruppenKopf({ label, anzahl, brutto, offen, aufgeklappt, onUmschalten, nachHaus }: {
  label: string; anzahl: number; brutto: string; offen: string; aufgeklappt: boolean; onUmschalten: () => void; nachHaus: boolean;
}) {
  const I = nachHaus ? HouseLine : Buildings;
  return (
    <button type="button" onClick={onUmschalten} aria-expanded={aufgeklappt}
      className="immo-glas flex w-full items-center text-left"
      style={{ gap: 10, minHeight: 56, padding: '12px 14px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)' }}>
      <CaretDownKlein offen={aufgeklappt} />
      <I size={16} weight="fill" color="var(--acc)" className="shrink-0" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{label}</span>
        <span className="truncate" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>
          {anzahl} {anzahl === 1 ? 'Beleg' : 'Belege'} · {brutto} · <span style={{ color: 'var(--warn)' }}>offen {offen}</span>
        </span>
      </span>
    </button>
  );
}

function CaretDownKlein({ offen }: { offen: boolean }) {
  return <CaretDown size={14} weight="bold" color="var(--ink3)" className="shrink-0" style={{ transition: 'transform .25s', transform: offen ? 'none' : 'rotate(-90deg)' }} />;
}

export function SeitenLeiste({ seite, seiten, onSeite }: { seite: number; seiten: number; onSeite: (s: number) => void }) {
  const knopf = (aus: boolean) => ({ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: aus ? 'default' : 'pointer', color: 'var(--ink)', opacity: aus ? 0.35 : 1 });
  return (
    <div className="flex items-center justify-center" style={{ gap: 12, padding: '6px 0' }}>
      <button type="button" className="immo-glas inline-flex items-center justify-center" aria-label="Vorherige Seite" data-tip="Vorherige Seite"
        disabled={seite === 1} onClick={() => onSeite(Math.max(1, seite - 1))} style={knopf(seite === 1)}>
        <CaretLeft size={16} weight="bold" />
      </button>
      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink2)' }}>Seite {seite} von {seiten}</span>
      <button type="button" className="immo-glas inline-flex items-center justify-center" aria-label="Nächste Seite" data-tip="Nächste Seite"
        disabled={seite === seiten} onClick={() => onSeite(Math.min(seiten, seite + 1))} style={knopf(seite === seiten)}>
        <CaretRight size={16} weight="bold" />
      </button>
    </div>
  );
}
